import Hls from 'hls.js'
import {
  Component,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react'
import {
  createHtmlPortalNode,
  InPortal,
  OutPortal,
  type HtmlPortalNode,
} from 'react-reverse-portal'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'

type VideoPortal = HtmlPortalNode<Component<JSX.IntrinsicElements['video']>>

export type VideoMetadata = {
  duration: number
}

interface VideoPlayerContextType {
  videoPortals: Record<string, Mutex<VideoPortal>>
  getVideoMetadata: (id: string) => VideoMetadata | undefined
  getVideoElement: (id: string) => HTMLVideoElement | undefined
}

export const VideoPlayerContext = createContext<
  VideoPlayerContextType | undefined
>(undefined)

/** Per-document URL pair for the HLS playlist. The player tries `primary` first; on a 404 from
 * the manifest load it falls back to `secondary`. In production this is `(cdn, legacy)` — the
 * CDN endpoint serves the cookie path, the legacy endpoint serves the GCS-signed-URL path. In
 * dev the CDN endpoint 404s and the player swaps to the legacy URL transparently.
 */
export interface VideoSrcUrls {
  primary: string
  secondary: string
}

interface VideoPlayerScopeProps {
  videoSrcMap: Record<string, VideoSrcUrls>
  children: React.ReactNode
}

function useVideo(
  video: Mutex<VideoPortal> | undefined,
  owner: boolean,
): VideoPortal | undefined {
  const [videoElement, setVideoElement] = useState<VideoPortal | undefined>(
    undefined,
  )
  useEffect(() => {
    if (video && owner) {
      video.withLock(async (node) => setVideoElement(node))
    } else {
      setVideoElement(undefined)
    }

    return () => {
      setVideoElement(undefined)
    }
  }, [video, owner])
  return videoElement
}

export function VideoPlayerScope({
  videoSrcMap,
  children,
}: VideoPlayerScopeProps) {
  const { idToken } = useIdTokenAndTenantId()
  const videoRefs = useRef<Record<string, HTMLVideoElement>>({})
  const videoPortals = useMemo(() => {
    console.debug('videoSrcMap', videoSrcMap)
    const portals: Record<string, Mutex<VideoPortal>> = {}
    for (const id of Object.keys(videoSrcMap)) {
      portals[id] = new Mutex(createHtmlPortalNode())
    }
    return portals
  }, [videoSrcMap])

  const videoMetadata = useRef<Record<string, VideoMetadata>>({})
  console.debug('videoPortals', videoPortals)

  useEffect(() => {
    const hlsMap: Record<string, Hls> = {}
    for (const [id, urls] of Object.entries(videoSrcMap)) {
      if (videoRefs.current[id]) {
        // Track which URL is currently loaded so the 403 handler reloads the right one and the
        // 404-fallback handler only swaps once.
        let currentUrl = urls.primary
        let triedFallback = false
        const hls = new Hls({
          // hls.js makes its own XHRs and bypasses our axios interceptor, so the auth header
          // must be wired here for the playlist call to api.{env}.linewise.io. The CDN segment
          // URLs in the playlist already carry their own signature query string, so the auth
          // header is unnecessary there — and sending it would force a CORS preflight that
          // GCS's responseHeader allowlist doesn't accept (no Authorization). We attach
          // Authorization only on requests to the API host.
          xhrSetup: (xhr, url) => {
            if (idToken && url.includes('/api/org/')) {
              xhr.setRequestHeader('Authorization', `Bearer ${idToken}`)
            }
          },
        })
        hls.on(Hls.Events.ERROR, (_event, data) => {
          const status = data.response?.code
          // 404 on the primary URL means CDN HLS is not configured in this env; swap to the
          // legacy fallback once. We only attempt this on the primary URL — a 404 on the
          // fallback is a real error.
          if (
            status === 404 &&
            !triedFallback &&
            currentUrl === urls.primary &&
            urls.secondary !== urls.primary
          ) {
            triedFallback = true
            currentUrl = urls.secondary
            console.info(
              `[hls] primary playlist 404 — falling back to ${urls.secondary}`,
            )
            hls.loadSource(currentUrl)
            return
          }
          // 403 on a fragment fetch means the CDN cookie expired. Re-loading the playlist URL
          // re-hits the API, which mints a fresh cookie via Set-Cookie. No-op on the legacy
          // path where segment URLs already carry their own 24h GCS signature.
          if (status === 403) {
            console.warn('[hls] 403 on segment fetch — refreshing playlist')
            hls.loadSource(currentUrl)
          }
        })
        hls.loadSource(currentUrl)
        hls.attachMedia(videoRefs.current[id])
        hlsMap[id] = hls
      }
    }
    return () => {
      for (const hls of Object.values(hlsMap)) {
        hls.destroy()
      }
    }
  }, [videoSrcMap, idToken])

  const getVideoElement = useCallback((id: string) => {
    return videoRefs.current[id]
  }, [])

  const getVideoMetadata = useCallback((id: string) => {
    return videoMetadata.current[id]
  }, [])

  return (
    <>
      {Object.entries(videoPortals).map(([src, portal]) => (
        <InPortal key={src} node={portal.value}>
          <video
            ref={(ref) => {
              if (ref) {
                videoRefs.current[src] = ref
              }
            }}
            onLoadedMetadata={(e) => {
              const video = e.currentTarget
              videoMetadata.current[src] = {
                duration: video.duration,
              }
            }}
          />
        </InPortal>
      ))}
      <VideoPlayerContext
        value={{ videoPortals, getVideoElement, getVideoMetadata }}
      >
        {children}
      </VideoPlayerContext>
    </>
  )
}

interface VideoPlayerTargetProps {
  placeholder: React.ReactNode
  videoSrcId: string
  keyframe?: number
  startTime: number
  endTime: number
  playing: boolean
  owner?: boolean
  className?: string
  style?: React.CSSProperties
  fullScreen?: boolean
  onEnded?: () => void
  onLoadedMetadata?: (metadata: VideoMetadata) => void
  onTimeUpdate?: (currentTime: number) => void
  onSeeked?: () => void
  onSeeking?: () => void
  onFirstTimePlaying?: () => void
  ref?: React.Ref<HTMLVideoElement>
}

export function VideoPlayerTarget({
  placeholder,
  videoSrcId,
  keyframe,
  startTime,
  endTime,
  playing,
  className,
  style,
  owner = true,
  fullScreen = false,
  onEnded,
  onTimeUpdate,
  onLoadedMetadata,
  onSeeked,
  onSeeking,
  onFirstTimePlaying,
  ref,
}: VideoPlayerTargetProps) {
  const context = useContext(VideoPlayerContext)

  if (!context) {
    throw new Error('VideoPlayerTarget must be used within a VideoPlayerScope')
  }
  const { videoPortals } = context

  const portal = useVideo(videoPortals[videoSrcId], owner)

  const currentTime = useRef(keyframe ?? startTime)
  const hasPlayedOnce = useRef(false)

  // Reset hasPlayedOnce when portal changes (new video instance)
  useEffect(() => {
    if (portal) {
      hasPlayedOnce.current = false
    }
  }, [portal])

  console.assert(
    !!videoPortals[videoSrcId],
    'videoPortals[videoSrcId] is undefined',
  )

  const updateCurrentTime = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      if (!playing && !owner) return
      const video = event.currentTarget
      currentTime.current = video.currentTime
      if (video.currentTime > endTime) {
        console.log('video.currentTime >= endTime', video.currentTime, endTime)
        video.currentTime = startTime
        video.pause()
        onEnded?.()
      }
      onTimeUpdate?.(currentTime.current)
    },
    [playing, owner, onEnded, onTimeUpdate, endTime, startTime],
  )

  useEffect(() => {
    if (portal && owner) {
      const video = portal.element.children[0] as HTMLVideoElement
      if (!video) return
      video.currentTime = currentTime.current
      if (playing) {
        if (!hasPlayedOnce.current) {
          onFirstTimePlaying?.()
          hasPlayedOnce.current = true
        }
        video.play()
      } else {
        video.pause()
      }
    }
  }, [portal, playing, owner, onFirstTimePlaying])

  useEffect(() => {
    if (portal && owner && fullScreen) {
      portal?.element.children[0].requestFullscreen()
    }
  }, [portal, owner, fullScreen])

  useEffect(() => {
    if (!portal) return
    const video = portal.element.children[0] as HTMLVideoElement
    if (!video) return
    if (Number.isNaN(video.duration) || video.duration === 0) return
    console.debug('video.duration', video.duration)
    onLoadedMetadata?.({
      duration: video.duration,
    })
  }, [portal, onLoadedMetadata])

  useEffect(() => {
    if (!ref) return
    if (typeof ref === 'function') {
      ref(portal?.element.children[0] as HTMLVideoElement)
    } else {
      ref.current = portal?.element.children[0] as HTMLVideoElement
    }
  }, [ref, portal])

  const handleLoadedMetadata = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget
      onLoadedMetadata?.({
        duration: video.duration,
      })
    },
    [onLoadedMetadata],
  )

  if (!portal) {
    return (
      <div className={className} style={style}>
        {placeholder}
      </div>
    )
  } else {
    return (
      <OutPortal<Component<JSX.IntrinsicElements['video']>>
        node={portal}
        onTimeUpdate={updateCurrentTime}
        className={className}
        style={style}
        onLoadedMetadata={handleLoadedMetadata}
        onSeeked={onSeeked}
        onSeeking={onSeeking}
      />
    )
  }
}

class Mutex<T> {
  private _locked: boolean = false
  private _queue: ((value: T) => void)[] = []
  private _value: T

  constructor(value: T) {
    this._value = value
  }

  get value(): T {
    return this._value
  }

  async acquire(): Promise<T> {
    if (this._locked) {
      await new Promise<T>((resolve) => {
        this._queue.push(() => resolve(this._value))
      })
    }
    this._locked = true
    return this._value
  }

  release() {
    this._locked = false
    if (this._queue.length > 0) {
      const resolve = this._queue.shift()
      if (resolve) {
        resolve(this._value)
      }
    }
  }

  async withLock<R>(fn: (value: T) => Promise<R>): Promise<R> {
    const value = await this.acquire()
    try {
      return await fn(value)
    } finally {
      this.release()
    }
  }
}
